import { type License, verifyLicense } from '../license/index.js';
import { MobiLauncherConfig } from '../schema/index.js';

/**
 * Result of loading + validating one of the runtime's input sources.
 * Discriminated union so callers can branch without try/catch ergonomics.
 *
 * Per "fail to Core": loader functions never throw on bad input. They
 * surface every failure as a structured `LoadFailure` so the engine can
 * route it to the config-error screen without UI-layer guessing.
 */
export type LoadResult<T> = { ok: true; value: T } | { ok: false; failure: LoadFailure };

export type LoadFailure =
  | { kind: 'parseJson'; message: string }
  | { kind: 'schema'; message: string; issues: unknown }
  | { kind: 'assetHashMismatch'; expected: string; actual: string }
  | { kind: 'licenseInvalid' }
  /** The signed bundle manifest failed verification — bad signature, or a
   *  bundled file whose hash doesn't match. The launcher refuses to boot a
   *  tampered bundle. */
  | { kind: 'bundleTamper'; message: string }
  /** Fetch threw before any response arrived — typically offline, DNS, or CORS. */
  | { kind: 'network'; message: string; offline: boolean }
  /** Server returned a non-2xx response. `status` is the HTTP status code. */
  | { kind: 'httpError'; status: number; message: string };

/**
 * Parse a config JSON string and validate against the schema. Pure — no
 * filesystem access. The caller (production: Phase 4 remote-config; tests:
 * fixture loaders) is responsible for IO.
 */
export function loadConfigFromString(raw: string): LoadResult<MobiLauncherConfig> {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch (err) {
    return { ok: false, failure: { kind: 'parseJson', message: asMessage(err) } };
  }
  const parsed = MobiLauncherConfig.safeParse(json);
  if (!parsed.success) {
    return {
      ok: false,
      failure: {
        kind: 'schema',
        message: parsed.error.message,
        issues: parsed.error.format(),
      },
    };
  }
  return { ok: true, value: parsed.data };
}

/**
 * Verify the on-disk asset bundle hash matches `config.meta.assetBundleHash`.
 * The actual hash is computed by the caller (production walks the bundle
 * directory; tests inject a constant). Returns a LoadResult so the engine
 * can show a config-error screen on mismatch — we do NOT render UI from
 * possibly-tampered assets.
 */
export function verifyAssetBundleHash(input: {
  expected: string;
  actual: string;
}): LoadResult<true> {
  if (input.expected === input.actual) {
    return { ok: true, value: true };
  }
  return {
    ok: false,
    failure: {
      kind: 'assetHashMismatch',
      expected: input.expected,
      actual: input.actual,
    },
  };
}

/**
 * Parse a license JSON string and verify it against the public key + tenant.
 * Returns `null` (NOT an error result) on any verification failure — that's
 * the documented "fail to Core" behavior. The engine keeps running as Core
 * and surfaces an "unlicensed" badge in the diagnostic overlay.
 */
export async function loadLicenseFromString(input: {
  raw: string;
  publicKey: Uint8Array;
  /** keyId → public key, forwarded to {@link verifyLicense} for key rotation. */
  keyring?: Record<string, Uint8Array>;
  expectedTenantId: string;
  now?: () => number;
  /** Anti-rollback clock floor forwarded to {@link verifyLicense}. */
  clockFloor?: number;
  /** Revoked license ids forwarded to {@link verifyLicense}. */
  revokedIds?: ReadonlySet<string>;
}): Promise<License | null> {
  let json: unknown;
  try {
    json = JSON.parse(input.raw);
  } catch {
    return null;
  }
  return verifyLicense({
    publicKey: input.publicKey,
    license: json,
    expectedTenantId: input.expectedTenantId,
    ...(input.keyring !== undefined ? { keyring: input.keyring } : {}),
    ...(input.now ? { now: input.now } : {}),
    ...(input.clockFloor !== undefined ? { clockFloor: input.clockFloor } : {}),
    ...(input.revokedIds !== undefined ? { revokedIds: input.revokedIds } : {}),
  });
}

function asMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
